import { LinxRequestRowType, RegionSteps } from '@shared/models';

import { LinxEntityType, LinxNotificationChannel, LinxSteps } from './linx-constants';
import { getRegionReasonLabel } from './region-labels';

const { CUSTOMER, COUNTERPARTY } = LinxEntityType;

const FINANCIAL_ACCOUNT_STEPS = [
  LinxSteps.SelectCountry,
  LinxSteps.CreateIndividualFinancialAccount,
  LinxSteps.LookupIndividualFinancialAccount,
  LinxSteps.LinkOrCreateFinancialAccount,
  LinxSteps.SelectOrCreateFinancialAccount,
  LinxSteps.LookupOrVerifyFinancialAccount,
  // Debit authorization is only reachable through a financial account or move money.
  LinxSteps.CreateDebitAuthorization,
  LinxSteps.LookupOrCreateDebitAuthorization,
];

// The backend step names each palette option can emit via getTemplateCreationPayload; a spec fails when the payload gains a step missing here.
// shortcut: individual and business variants are not told apart, upgrade if a region ever makes only one of them unavailable.
export const PALETTE_OPTION_STEPS: Partial<Record<LinxRequestRowType, Record<LinxEntityType, LinxSteps[]>>> = {
  'enhance-profile': {
    [CUSTOMER]: [LinxSteps.EnhanceIndividualProfile, LinxSteps.EnhanceBusinessProfile, LinxSteps.CollectDocuments],
    [COUNTERPARTY]: [LinxSteps.EnhanceCounterparty, LinxSteps.CollectDocuments],
  },
  'enhance-beneficiary': {
    [CUSTOMER]: [LinxSteps.CreateBeneficiary, LinxSteps.LookupBeneficiary, LinxSteps.EnhanceBeneficiary, LinxSteps.CollectDocuments],
    [COUNTERPARTY]: [],
  },
  'identity-verification': {
    [CUSTOMER]: [LinxSteps.SubmitBusinessVerification, LinxSteps.SubmitIndividualVerification],
    [COUNTERPARTY]: [],
  },
  'financial-account': { [CUSTOMER]: FINANCIAL_ACCOUNT_STEPS, [COUNTERPARTY]: FINANCIAL_ACCOUNT_STEPS },
  'document-acceptance': { [CUSTOMER]: [LinxSteps.DocumentAcceptance], [COUNTERPARTY]: [] },
  'move-money': {
    [CUSTOMER]: [LinxSteps.MoveMoney, ...FINANCIAL_ACCOUNT_STEPS],
    [COUNTERPARTY]: [LinxSteps.MoveMoney, ...FINANCIAL_ACCOUNT_STEPS],
  },
};

export const ENTITY_STEPS: Record<LinxEntityType, LinxSteps[]> = {
  [CUSTOMER]: [
    LinxSteps.CreateIndividualAccount,
    LinxSteps.LookupIndividualProfile,
    LinxSteps.CreateBusinessAccount,
    LinxSteps.LookupBusinessProfile,
  ],
  [COUNTERPARTY]: [LinxSteps.CreateCounterparty, LinxSteps.LookupCounterparty],
};

const firstUnavailableReason = (stepNames: string[], steps: RegionSteps): string | undefined => {
  const unavailable = steps.unavailableSteps.find(({ step }) => stepNames.includes(step));
  return unavailable ? getRegionReasonLabel(unavailable.reason) : undefined;
};

export const getUnavailablePaletteOptions = (
  steps: RegionSteps | undefined,
  entityType: LinxEntityType | undefined,
): Partial<Record<LinxRequestRowType, string>> => {
  const result: Partial<Record<LinxRequestRowType, string>> = {};
  if (!steps || !entityType) {
    return result;
  }
  (Object.entries(PALETTE_OPTION_STEPS) as [LinxRequestRowType, Record<LinxEntityType, LinxSteps[]>][]).forEach(([option, byEntity]) => {
    const reason = firstUnavailableReason(byEntity[entityType], steps);
    if (reason) {
      result[option] = reason;
    }
  });
  return result;
};

export const getUnavailableEntityReason = (steps: RegionSteps | undefined, entityType: LinxEntityType): string | undefined =>
  steps ? firstUnavailableReason(ENTITY_STEPS[entityType], steps) : undefined;

const CHANNEL_LABELS = {
  [LinxNotificationChannel.Email]: 'Email',
  [LinxNotificationChannel.Sms]: 'SMS',
  [LinxNotificationChannel.ALL]: 'All',
};

// The reason sits in the label so it stays visible text in the radio group.
export const getNotificationChannelOptions = (steps?: RegionSteps): { label: string; value: string; disabled?: boolean }[] => {
  const channels = [LinxNotificationChannel.Email, LinxNotificationChannel.Sms];
  if (!steps) {
    return [...channels, LinxNotificationChannel.ALL].map((value) => ({ label: CHANNEL_LABELS[value], value }));
  }
  const sameChannel = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();
  const options = channels.map((value) => {
    if (steps.availableChannels.some((channel) => sameChannel(channel, value))) {
      return { label: CHANNEL_LABELS[value], value, disabled: false };
    }
    const unavailable = steps.unavailableChannels.find(({ channel }) => sameChannel(channel, value));
    return { label: `${CHANNEL_LABELS[value]} (${getRegionReasonLabel(unavailable?.reason)})`, value, disabled: true };
  });
  return options.every(({ disabled }) => !disabled)
    ? [...options, { label: CHANNEL_LABELS[LinxNotificationChannel.ALL], value: LinxNotificationChannel.ALL, disabled: false }]
    : options;
};

// A step error's body does not say which palette row it is about, so rows are matched by step name in the error text.
export const getPaletteOptionsNamedIn = (text: string): LinxRequestRowType[] =>
  (Object.entries(PALETTE_OPTION_STEPS) as [LinxRequestRowType, Record<LinxEntityType, LinxSteps[]>][])
    .filter(([, byEntity]) =>
      Object.values(byEntity)
        .flat()
        .some((step) => new RegExp(`(^|[^a-z-])${step}([^a-z-]|$)`).test(text)),
    )
    .map(([option]) => option);
